import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useApi } from '../hooks/useApi';
import { useMsal } from '@azure/msal-react';

const ServicesContext = createContext();

export function ServicesProvider({ children }) {
  const { apiCall } = useApi();
  const { accounts } = useMsal();
  const [services, setServices] = useState([]);
  const [pendingServiceChanges, setPendingServiceChanges] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isAuthenticated = accounts && accounts.length > 0;

  const loadPendingServiceChanges = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const result = await apiCall('services-schedule-list?pending=true');
      const byId = {};
      for (const row of result.schedule || []) {
        if (!byId[row.service_id] || row.effective_date < byId[row.service_id].effective_date) {
          byId[row.service_id] = row;
        }
      }
      setPendingServiceChanges(byId);
    } catch (err) {
      console.error('Failed to load pending service price changes:', err);
    }
  }, [apiCall, isAuthenticated]);

  const loadServices = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    setError(null);
    try {
      const [result] = await Promise.all([
        apiCall('services-list?includeInactive=true'),
        loadPendingServiceChanges(),
      ]);
      setServices(result.services || []);
    } catch (err) {
      console.error('Failed to load services:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, isAuthenticated, loadPendingServiceChanges]);

  const createService = useCallback(async (data) => {
    const result = await apiCall('services-create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    await loadServices();
    return result.service;
  }, [apiCall, loadServices]);

  const updateService = useCallback(async (id, data) => {
    const result = await apiCall(`services-update?id=${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    await loadServices();
    return result.service;
  }, [apiCall, loadServices]);

  const deleteService = useCallback(async (id) => {
    const result = await apiCall(`services-delete?id=${id}`, {
      method: 'DELETE',
    });
    await loadServices();
    return result;
  }, [apiCall, loadServices]);

  const scheduleServicePriceChange = useCallback(async ({ service_id, cost_per_unit, gross_margin_rate, price_per_unit, effective_date }) => {
    const result = await apiCall('services-schedule-create', {
      method: 'POST',
      body: JSON.stringify({ service_id, cost_per_unit, gross_margin_rate, price_per_unit, effective_date }),
    });
    await loadServices();
    return result;
  }, [apiCall, loadServices]);

  const cancelPendingServiceChange = useCallback(async (scheduleId) => {
    const result = await apiCall(`services-schedule-delete?id=${scheduleId}`, {
      method: 'DELETE',
    });
    await loadPendingServiceChanges();
    return result;
  }, [apiCall, loadPendingServiceChanges]);

  const getServiceByCode = useCallback((code) =>
    services.find(s => s.code === code) || null,
    [services]
  );

  // Memoize so consumers don't re-render on every provider render (e.g. each
  // MSAL token refresh). Callbacks and setError are already stable; only the
  // state values change identity.
  const value = useMemo(() => ({
    services,
    pendingServiceChanges,
    loading,
    error,
    setError,
    loadServices,
    loadPendingServiceChanges,
    createService,
    updateService,
    deleteService,
    scheduleServicePriceChange,
    cancelPendingServiceChange,
    getServiceByCode,
  }), [
    services,
    pendingServiceChanges,
    loading,
    error,
    setError,
    loadServices,
    loadPendingServiceChanges,
    createService,
    updateService,
    deleteService,
    scheduleServicePriceChange,
    cancelPendingServiceChange,
    getServiceByCode,
  ]);

  return (
    <ServicesContext.Provider value={value}>
      {children}
    </ServicesContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useServicesContext() {
  const context = useContext(ServicesContext);
  if (!context) {
    throw new Error('useServicesContext must be used within a ServicesProvider');
  }
  return context;
}
